import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ApplyColumnStateParams, ColDef, ColumnApi, CsvExportParams, GridApi, GridReadyEvent, IsFullWidthRowParams, ProcessCellForExportParams } from 'ag-grid-community';
import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject, debounceTime, delay, distinctUntilChanged, map, Observable, Subscription, tap } from 'rxjs';
import { INVOICE_LIST_WARNING_COLOR_DAYS } from '../../constants/default.const';
import { SubComponentsToLoad } from '../../enums/components-to-load.enum';
import { DialogType } from '../../enums/dialog-type.enum';
import { ConsumerPortalConfig } from '../../models/consumer-portal-config';
import { ContractCP, GetDocumentParam } from '../../models/contract-model';
import { Invoice, InvoiceTotalAmountDue } from '../../models/invoice-model';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { AppFacadeService } from '../../services/app-facade.service';
import { AppMediatorService } from '../../services/app-mediator.service';
import { ConfigurationService } from '../../services/configuration.service';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { InvoicesService } from '../../services/invoices.service';
import { LoggerService } from '../../services/logger.service';
import { NotificationService } from '../../services/notification.service';
import { FilterOutput } from '../../shared/models/filters.model';
import { FormatAddressPipe } from '../../shared/pipes/format-address.pipe';
import { clearInvoicesinStore, increasePageNumberInvoices } from '../../store/domains/invoices/invoices.actions';
import { getInvoices, getNoMoreRecordInvoices } from '../../store/domains/invoices/invoices.selectors';
import { Address, formatDateType } from '../../types';
import { InvoicesFullWidthRowComponent } from '../invoices-full-width-row/invoices-full-width-row.component';
import { AmountRendererComponent } from './renderer/amount/amount-renderer.component';
import { ContractPdfResponse } from '../../models/pdf-response';

@Component({
    standalone: false,
    selector: 'por-invoices',
    templateUrl: './invoices.component.html',
    styleUrls: ['./invoices.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoicesComponent implements OnInit, OnDestroy {
    readonly appFacadeService = inject(AppFacadeService);
    private readonly store = inject(Store);
    private readonly invoiceService = inject(InvoicesService);
    private readonly notification = inject(NotificationService);
    private readonly configService = inject(ConfigurationService);
    private readonly datePipe = inject(FormatDatePipe);
    private readonly translateService = inject(TranslateService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly logger = inject(LoggerService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly formatAddressPipe = inject(FormatAddressPipe);

    currentWidth = window.innerWidth;
    public context;
    invoiceTotalAmountDue$!: Observable<number | undefined>;
    invoices$: Observable<Invoice[]> = this.store.select(getInvoices);
    noMoreRecord$: Observable<boolean> = this.store.select(getNoMoreRecordInvoices);
    customerId!: string;
    private readonly subscriptions: Subscription[] = [];
    gridApi!: GridApi;
    gridColumnApi!: ColumnApi;
    headerHeight = 35;
    rowHeight = 65;

    inputs: ConsumerPortalConfig | undefined;
    gridReady$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    gridReadyObservable$: Observable<boolean> = this.gridReady$.pipe(delay(1000));

    @Input() config: string | undefined;
    @Input() width: string | undefined;
    @Input() height: string | undefined;
    @Output() readonly elementLoaded = new EventEmitter<boolean>();
    @Output() readonly appOutput: EventEmitter<ContractCP[] | Invoice[]> = new EventEmitter<ContractCP[] | Invoice[]>();

    uiUrl: string | undefined;
    @Input() initialStatusFilters?: string[];
    filters: FilterOutput | undefined;

    dialogType: DialogType = DialogType.information;

    constructor() {
        const store = this.store;

        store.dispatch(clearInvoicesinStore());
        this.context = {
            componentParent: this
        };
    }

    public filterComponentProps: { searchPlaceHolder: string; dateLabel: string } = {
        searchPlaceHolder: this.translateService.instant('QuickSearch'),
        dateLabel: this.translateService.instant('SelectInvoiceDateRange')
    };

    public columnDefs: ColDef[] = [];
    public defaultColDef: ColDef = {
        sortable: true,
        resizable: true,
        suppressMovable: true,
        wrapText: false,
        sortingOrder: ['asc', 'desc'],
        suppressMenu: true
    };
    public fullWidthCellRenderer = InvoicesFullWidthRowComponent;

    public getScreenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);

    isFullWidth = false;

    public isFullWidthRow: (params: IsFullWidthRowParams) => boolean = () => {
        return this.isFullWidth;
    };

    //Need to check if it exceeds the breakpoint, then need to check responsive grid
    @HostListener('window:resize', ['$event'])
    onWindowResize() {
        if (this.currentWidth >= 1024 && window.innerWidth <= 1024) {
            this.getScreenWidth.next(window.innerWidth);
        } else if (this.currentWidth <= 1024 && window.innerWidth >= 1024) {
            this.getScreenWidth.next(window.innerWidth);
        }
        this.currentWidth = window.innerWidth;
    }

    private readonly formatDate = (params: formatDateType) => {
        const date = this.datePipe.transform(params.value, this.appFacadeService.featureToggleService.isSyrinx());
        return `<span class="theme-table-row">${date}</span>`;
    };

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.gridColumnApi = params.columnApi;
        this.gridApi.setDomLayout('autoHeight');
        const columnState: ApplyColumnStateParams = {
            state: [
                {
                    colId: 'InvoiceDate',
                    sort: 'desc'
                }
            ]
        };
        this.gridColumnApi.applyColumnState(columnState);
        this.gridApi.sizeColumnsToFit();
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        this.subscriptions.push(
            this.getScreenWidth.pipe(debounceTime(1000)).subscribe((width: number) => {
                this.setResponsiveGrid(width);
            })
        );
        this.configService.setMatIconFontFamily();
        if (this.config && !isEmpty(this.config)) {
            this.inputs = JSON.parse(this.config);
            this.logger.logAssert(this.inputs?.customerId, 'customerId is not defined');
            this.customerId = this.inputs?.customerId || '0';
        }
        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            this.elementLoaded.emit(false);
            return;
        }

        this.elementLoaded.emit(true);
        this.subscriptions.push(
            this.invoices$.subscribe(invoices => {
                if (!isEmpty(invoices)) {
                    this.appOutput.emit(invoices);
                }
            })
        );

        // checking window size
        this.subscriptions.push(
            this.breakpointObserver
                .observe(['(max-width: 992px)'])
                .pipe(
                    tap((value: BreakpointState) => {
                        if (value.matches) {
                            this.filterComponentProps = {
                                searchPlaceHolder: this.translateService.instant('SearchInvoices'),
                                dateLabel: this.translateService.instant('filterbyDate')
                            };
                        } else {
                            this.filterComponentProps = {
                                searchPlaceHolder: this.translateService.instant('QuickSearch'),
                                dateLabel: this.translateService.instant('SelectInvoiceDateRange')
                            };
                        }
                    }),
                    distinctUntilChanged()
                )
                .subscribe()
        );
        this.invoiceTotalAmountDue$ = this.invoiceService.getInvoiceTotalAmountDue(this.customerId).pipe(
            map((data: InvoiceTotalAmountDue[]) => {
                const a: InvoiceTotalAmountDue | null = data.length ? data[0] : null;
                return a?.TotalAmountDue;
            })
        );
        this.uiUrl = this.inputs?.uiUrl;
    }

    filter($e: FilterOutput) {
        this.filters = $e;
        this.invoiceService.filter($e, this.customerId);
    }

    loadMore() {
        if (!this.customerId) {
            this.notification.error(this.translateService.instant('customerIdNotConfigured'));
            return;
        }

        this.store.dispatch(increasePageNumberInvoices());
        this.invoiceService.getInvoices(this.customerId, this.filters?.dates?.startDate, this.filters?.dates?.endDate, this.filters?.search);
        this.gridApi.refreshCells();
    }

    exportCSV() {
        const visibleColumnKeys = this.gridColumnApi.getAllDisplayedColumns().map(column => column.getColId());
        const params: CsvExportParams = {
            skipHeader: false,
            skipGroups: true,
            fileName: 'Invoices.csv',
            columnKeys: visibleColumnKeys,
            processCellCallback: (param: ProcessCellForExportParams) => {
                return this.processCellCSV(param);
            }
        };
        this.gridApi.exportDataAsCsv(params);
    }

    processCellCSV(params: ProcessCellForExportParams): string {
        const colId = params.column.getColId();
        if (colId && ['InvoiceDate', 'DueDate'].includes(colId)) {
            return this.datePipe.transform(params.value, this.appFacadeService.featureToggleService.isSyrinx());
        }
        if (colId && ['AdditionalFields.Address'].includes(colId)) {
            return this.formatAddressPipe.format(params?.value);
        }
        return params.value;
    }

    setResponsiveGrid(width: number) {
        this.gridReady$.next(false);
        if (width > 1024) {
            this.columnDefs = [
                ...(this.featureToggleService.isAvailable('canSeeInvoiceContractIdCol')
                    ? [
                          {
                              field: 'Identifiers.ContractId',
                              headerName: this.translateService.instant('InvoiceColumnHeaderContract#'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header',
                              cellRenderer: (params: { value: number }) => {
                                  if (this.appFacadeService.featureToggleService.isAvailable('pdfDownloadFeature')) {
                                      return `<a href="javascript:void(0)" class="invoice-link">${params.value}</a>`;
                                  } else {
                                      return `<span class="theme-table-row">${params.value}</span>`;
                                  }
                                  return '';
                              },
                              onCellClicked: (params: { data: Invoice }) => {
                                  if (this.appFacadeService.featureToggleService.isAvailable('pdfDownloadFeature')) {
                                      if (params.data) {
                                          this.viewContractPdf(params.data);
                                      }
                                  }
                              }
                          }
                      ]
                    : []),
                {
                    field: 'InvoiceNumber',
                    headerName: this.translateService.instant('invoiceNumber'),
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header',
                    sortable: true,
                    cellRenderer: (params: { value: number }) => {
                        if (params.value) {
                            if (this.appFacadeService.featureToggleService.isAvailable('canSeeInvoiceDetails')) {
                                return `<a href="javascript:void(0)" class="invoice-link">${params.value}</a>`;
                            } else {
                                return `<span class="theme-table-row">${params.value}</span>`;
                            }
                        }
                        return '';
                    },
                    onCellClicked: params => {
                        if (params.value && this.appFacadeService.featureToggleService.isAvailable('canSeeInvoiceDetails')) {
                            this.loadInvoiceDetailModal(params.data);
                        }
                    }
                },
                ...(this.featureToggleService.isAvailable('canSeeInvoiceAmountDueCol')
                    ? [
                          {
                              field: 'AmountDue',
                              headerName: this.translateService.instant('InvoiceColumnHeaderAmountDue'),
                              cellClass: 'theme-table-row',
                              cellRenderer: AmountRendererComponent,
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceDepotCol')
                    ? [
                          {
                              field: 'DepotName',
                              headerName: this.translateService.instant('Depot'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),

                ...(this.featureToggleService.isAvailable('canSeeInvoiceSiteCol')
                    ? [
                          {
                              field: 'AdditionalFields.Address',
                              headerName: this.translateService.instant('location'),
                              minWidth: 230,
                              wrapText: true,
                              autoHeight: true,
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header',
                              cellRenderer: (params: { value: Address }) => {
                                  return this.formatAddressPipe.format(params?.value);
                              }
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceTypeCol')
                    ? [
                          {
                              field: 'Type',
                              headerName: this.translateService.instant('Type'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceDueDateCol')
                    ? [
                          {
                              field: 'DueDate',
                              headerName: this.translateService.instant('InvoiceColumnHeaderDueDate'),
                              cellClass: 'theme-table-row',
                              cellRenderer: this.formatDate,
                              cellStyle: (params: { data: Invoice }) => {
                                  if (params.data?.DueDate) {
                                      return this.getCellStyle(params.data.DueDate);
                                  }
                                  return null;
                              },
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceBilledDateCol')
                    ? [
                          {
                              field: 'InvoiceDate',
                              headerName: this.translateService.instant('InvoiceColumnHeaderBilledDate'),
                              cellClass: 'theme-table-row',
                              cellRenderer: this.formatDate,
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoicePOCol')
                    ? [
                          {
                              field: 'CustomerPurchaseOrderNumber',
                              headerName: this.translateService.instant('InvoiceColumnHeaderPO'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                ...(this.featureToggleService.isAvailable('canSeeInvoiceJobNumberCol')
                    ? [
                          {
                              field: 'CustomerJobNumber',
                              headerName: this.translateService.instant('InvoiceColumnHeaderJobNumber'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header'
                          }
                      ]
                    : []),
                {
                    field: 'GrandTotal',
                    headerName: this.translateService.instant('invoiceTotal'),
                    cellClass: 'theme-table-row',
                    headerClass: 'theme-table-header',
                    cellRenderer: AmountRendererComponent,
                    sortable: true
                },
                ...(this.featureToggleService.isAvailable('canSeeInvoiceGrandTaxTotalCol')
                    ? [
                          {
                              field: 'GrandTaxTotal',
                              headerName: this.translateService.instant('invoiceTax'),
                              cellClass: 'theme-table-row',
                              headerClass: 'theme-table-header',
                              cellRenderer: AmountRendererComponent,
                              sortable: true
                          }
                      ]
                    : [])
            ];
            this.headerHeight = 35;
            this.isFullWidth = false;
            this.rowHeight = 65;
        }
        if (width < 1024) {
            this.columnDefs = [];
            this.headerHeight = 0;
            this.isFullWidth = true;
            this.rowHeight = this.rowHeight = this.appFacadeService.featureToggleService.isSyrinx() ? 275 : 340;
        }
        this.gridReady$.next(true);
    }

    loadInvoiceDetailModal(data: Invoice) {
        this.appFacadeService.fetchInvoiceDetail(this.customerId, data.Id);
    }

    closeInvoiceDetail() {
        this.appFacadeService.setSubActiveTab(SubComponentsToLoad.Default);
        this.appFacadeService.clearInvoiceDetail();
    }

    viewInvoicePdf(data: Invoice) {
        if (this.appFacadeService.featureToggleService.isAvailable('pdfDownloadFeature')) {
            const organizationType: string = this.appMediatorService.localStorageService?.getCurrentUser?.organizationType as string;
            const contractdetails: GetDocumentParam = {
                customerId: data?.CustomerId ?? '0',
                contractId: data.Id, // InvoiceId == contractId
                orgType: organizationType,
                documentValue: data.Id
            };
            this.appFacadeService.downLoadInvoiceDocument(contractdetails, data.Id);
        }
    }

    viewContractPdf(data: Invoice): void {
        this.appFacadeService.setLoading(true);
        this.subscriptions.push(
            this.appFacadeService.pdf.downloadPdf(this.customerId, data.Identifiers?.ContractId, 'Contract', data.Identifiers?.ContractId).subscribe((response: ContractPdfResponse) => {
                const url = response?.SecureURL || response?.URL;
                if (url) {
                    this.appFacadeService.pdf.openPdfinNewTab(url);
                }
                this.appFacadeService.setLoading(false);
            })
        );
    }

    getCellStyle(invoiceDueDate: string): { [klass: string]: string } {
        if (this.appFacadeService.featureToggleService.isSyrinx() || !invoiceDueDate) {
            return {};
        }

        // if DueDate is past today, then row will be red
        const dueDate = new Date(invoiceDueDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison
        dueDate.setHours(0, 0, 0, 0); // Set time to midnight for accurate comparison

        // if duedate is more than one day in future
        if (dueDate < today) {
            return { backgroundColor: '#f7ebeb' };
        }

        // if duedate is same as today or 14 days in future, then row will be yellow
        if (dueDate.getTime() - INVOICE_LIST_WARNING_COLOR_DAYS * 24 * 60 * 60 * 1000 <= today.getTime()) {
            return { backgroundColor: '#f5f5eb' };
        }

        return {};
    }
}
